import React, { StrictMode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router";
import { LoginWall } from "components/App/components/LoginWall";
import { CLITokenGenerationPage } from "components/pages/CLITokenGenerationPage";
import { compiledClientConfig } from "config";
import { BetaContext } from "context/betaContext";
import { PageProvider as NewPageProvider } from "context/new/pageContext";
import { PageProvider } from "context/pageContext";
import { useGlobalStyles } from "globalStyles";
import { devLog } from "utils/devtools/devLogging";
import { gainsightInit } from "utils/gainsight";
import { logrocketLogError } from "utils/logrocket";
import { checkForNewVersionEventEmitter, CHECK_EVENT } from "utils/version";
import { GlobalErrorModal } from "./components/GlobalErrorModal";
import { Layout } from "./components/Layout";
import { MicrosoftTeamsRouter } from "./components/MicrosoftTeamsRouter";
import { Providers } from "./Providers";
import { useStyles } from "./styles";

const GAINSIGHT_SDK_URL = "https://web-sdk.aptrinsic.com/api/aptrinsic.js";

const getLastCommitHash = async () => {
	const data = await (await fetch("/deploymentInfo")).json();
	return data["COMMIT_HASH"] as string;
};

const useKeepAppUpToDate = (production: boolean) => {
	const lastCommitHashRef = useRef<string>();
	const intervalRef = useRef<number>();

	const checkUpdatedCommitHash = useCallback(async () => {
		try {
			if (lastCommitHashRef.current && lastCommitHashRef.current !== (await getLastCommitHash())) {
				window.location.reload();
			}
		} catch (error) {
			devLog({ message: "failed to fetch last commit hash, will try again soon", level: "error" });
			logrocketLogError(
				error instanceof Error ? error : new Error("failed to fetch last commit hash, will try again soon")
			);
		}
	}, []);
	const update = useCallback(async () => {
		try {
			lastCommitHashRef.current = await getLastCommitHash();

			if (intervalRef.current) window.clearInterval(intervalRef.current);

			intervalRef.current = window.setInterval(
				() => void checkUpdatedCommitHash(),
				compiledClientConfig.upToDateCheckInterval
			);
		} catch (error) {
			devLog({ message: "failed to fetch last commit hash, client won't refresh automatically", level: "error" });
			logrocketLogError(
				error instanceof Error
					? error
					: new Error("failed to fetch last commit hash, client won't refresh automatically")
			);
		}
	}, [checkUpdatedCommitHash]);

	useEffect(() => {
		if (production) void update();
		return () => {
			if (intervalRef.current) window.clearInterval(intervalRef.current);
		};
	}, [production, update]);

	useEffect(() => {
		const visibilityChange = () => {
			if (document.visibilityState === "visible") {
				void checkUpdatedCommitHash();
			}
		};
		document.addEventListener("visibilitychange", visibilityChange);
		return () => document.removeEventListener("visibilitychange", visibilityChange);
	}, [checkUpdatedCommitHash]);

	useEffect(() => {
		const callback = () => {
			void checkUpdatedCommitHash();
		};
		checkForNewVersionEventEmitter.on(CHECK_EVENT, callback);

		return () => {
			checkForNewVersionEventEmitter.off(CHECK_EVENT, callback);
		};
	}, [checkUpdatedCommitHash]);
};

const useGainsightPX = () => {
	useEffect(() => {
		const clientId = compiledClientConfig.gainsightClientId;
		if (!clientId) return;

		if (!window.aptrinsic) {
			const aptrinsicFunc: Aptrinsic = function (...args) {
				(aptrinsicFunc.q = aptrinsicFunc.q || []).push(args);
			};
			aptrinsicFunc.p = clientId;
			aptrinsicFunc.c = undefined;

			window.aptrinsic = aptrinsicFunc;
		}

		const script = document.createElement("script");
		script.async = true;
		script.src = `${GAINSIGHT_SDK_URL}?a=${clientId}`;

		script.onload = () => {
			void gainsightInit();
		};
		const firstScript = document.getElementsByTagName("script")[0];
		firstScript.parentNode?.insertBefore(script, firstScript);
	}, []);
};

const Routers: FC = () => {
	const {
		actions: { setBeta }
	} = BetaContext();

	useEffect(() => {
		setBeta(true);
	}, [setBeta]);

	useGainsightPX();

	return (
		<BrowserRouter>
			<PageProvider>
				<NewPageProvider>
					<MicrosoftTeamsRouter>
						<LoginWall>
							<Routes>
								<Route path="/cliLogin" key={"CLILoginGeneration"} element={<CLITokenGenerationPage />} />
								<Route path="*" element={<Layout />} />
							</Routes>
						</LoginWall>
					</MicrosoftTeamsRouter>
				</NewPageProvider>
			</PageProvider>
		</BrowserRouter>
	);
};

export default function App() {
	const { t } = useTranslation();
	const classes = useStyles();
	useGlobalStyles();
	useKeepAppUpToDate(compiledClientConfig.isProd);

	useEffect(() => {
		const direction = t("direction");

		document.body.classList.toggle("ltr", direction === "ltr");
		document.body.classList.toggle("rtl", direction === "rtl");
	}, [t]);

	return (
		<div className={classes.app}>
			<StrictMode>
				<Providers>
					<GlobalErrorModal />
					<Routers />
				</Providers>
			</StrictMode>
		</div>
	);
}
